import { apiSlice } from '../../app/api/apiSlice'
import { urlConfig } from '../../config'
import { WithdrawalSource } from '../withdraws/source'

const base = `${urlConfig.walletsBaseUrl}/wallets/momo`

// Backend: apps/wallets/src/momo (MomoController). Ghana mobile money paid out
// in GH₵ from the naira balance; a separate path from /wallets/withdraws.

export interface MomoNetwork {
  id: string
  name: string
}

// GET /config — feature flag, networks, rate and bounds for the form.
export type MomoConfig =
  | { enabled: false; networks: [] }
  | {
      enabled: true
      networks: MomoNetwork[]
      fxRate: number // naira per cedi
      minGhs: number
      maxGhs: number
      minNgn: number
    }

export interface MomoResolveResponse {
  network: MomoNetwork
  msisdn: string // normalised 0XXXXXXXXX
  accountName: string
}

export interface MomoQuoteResponse {
  amountNgn: number
  fxRate: number
  payoutAmountGhs: number
  minGhs: number
  maxGhs: number
}

export type MomoStatus =
  | 'pending'
  | 'awaiting_approval'
  | 'submitting'
  | 'processing'
  | 'completed'
  | 'manual_review'
  | 'refunded'

export interface MomoWithdrawal {
  _id: string
  status: MomoStatus
  // account (MAIN, default) | reward | referral
  source?: WithdrawalSource
  amountNgn: number
  payoutAmountGhs: number
  fxRate: number
  network: MomoNetwork
  msisdn: string
  accountName: string
  createdAt: string
  completedAt?: string
  refundedAt?: string
}

export interface CreateMomoWithdrawalBody {
  networkId: string
  phone: string
  amountNgn: number
  // The GH₵ figure the user confirmed; the backend refuses (409) if the rate
  // moved so the payout would differ.
  expectedPayoutGhs?: number
  // Which balance pays; defaults to account on the server.
  source?: WithdrawalSource
}

export const momoSlice = apiSlice.injectEndpoints({
  endpoints: build => ({
    getMomoConfig: build.query<MomoConfig, void>({
      query: () => `${base}/config`,
    }),

    resolveMomo: build.mutation<MomoResolveResponse, { networkId: string; phone: string }>({
      query: body => ({ url: `${base}/resolve`, method: 'POST', body }),
    }),

    getMomoQuote: build.mutation<MomoQuoteResponse, { amountNgn: number }>({
      query: body => ({ url: `${base}/quote`, method: 'POST', body }),
    }),

    // Money-moving -> 2FA + OTP on the backend. The first call (no OTP headers)
    // returns a 401 "OTP required" challenge; resubmit with
    // createMomoWithdrawalWithOtp. Mirrors createWithdraw/-WithOtp.
    createMomoWithdrawal: build.mutation<MomoWithdrawal, CreateMomoWithdrawalBody>({
      query: body => ({ url: `${base}/withdrawals`, method: 'POST', body }),
      invalidatesTags: [{ type: 'MomoWithdrawals', id: 'PARTIAL-LIST' }, 'Wallet'],
    }),

    createMomoWithdrawalWithOtp: build.mutation<
      MomoWithdrawal,
      { body: CreateMomoWithdrawalBody; challengeId: string; otpCode: string }
    >({
      query: ({ body, challengeId, otpCode }) => ({
        url: `${base}/withdrawals`,
        method: 'POST',
        body,
        headers: { 'x-otp-challenge': challengeId, 'x-otp-code': otpCode },
      }),
      invalidatesTags: [{ type: 'MomoWithdrawals', id: 'PARTIAL-LIST' }, 'Wallet'],
    }),

    listMyMomoWithdrawals: build.query<
      { data: MomoWithdrawal[]; total: number; page: number; limit: number },
      { page?: number; limit?: number } | void
    >({
      query: params => {
        const page = params?.page ?? 1
        const limit = params?.limit ?? 20
        return `${base}/withdrawals?page=${page}&limit=${limit}`
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(w => ({ type: 'MomoWithdrawals' as const, id: w._id })),
              { type: 'MomoWithdrawals', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'MomoWithdrawals', id: 'PARTIAL-LIST' }],
    }),

    getMomoWithdrawalById: build.query<MomoWithdrawal, string>({
      query: id => `${base}/withdrawals/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'MomoWithdrawals', id }],
    }),
  }),
})

export const {
  useGetMomoConfigQuery,
  useResolveMomoMutation,
  useGetMomoQuoteMutation,
  useCreateMomoWithdrawalMutation,
  useCreateMomoWithdrawalWithOtpMutation,
  useListMyMomoWithdrawalsQuery,
  useGetMomoWithdrawalByIdQuery,
} = momoSlice
